import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageTransition from '../../components/PageTransition';
import SafeIcon from '../../common/SafeIcon';
import { FiArrowLeft, FiCheck, FiPlus, FiMap, FiGrid } from 'react-icons/fi';
import { supabase, TABLES } from '../../supabase/supabase';
import JourneyMapConfig from '../../components/modules/JourneyMapConfig';
import ExperienceMatrixConfig from '../../components/modules/ExperienceMatrixConfig';

const ModuleBuilder = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState(null); // 'journey_map' or 'xy_plot'

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data: pData } = await supabase.from(TABLES.PROJECTS).select('name').eq('id', projectId).single();
      setProject(pData);

      const { data: mData } = await supabase.from(TABLES.MODULES).select('*').eq('project_id', projectId);
      setModules(mData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const hasModule = (type) => modules.find(m => m.type === type);

  if (loading) return (
    <div className="h-full flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rutabaga-pink"></div>
    </div>
  );

  return (
    <PageTransition className="max-w-5xl mx-auto p-12">
      <div className="flex items-center justify-between mb-12">
        <div>
          <button 
            onClick={() => navigate('/projects')}
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-rutabaga-dark transition-colors mb-4 font-bold"
          >
            <SafeIcon icon={FiArrowLeft} /> Back to Projects
          </button>
          <h1 className="text-4xl font-black text-rutabaga-dark tracking-tight">
            Module Builder
          </h1>
          <p className="text-gray-500 font-medium mt-1">
            Project: <span className="text-rutabaga-pink font-bold">{project?.name}</span>
          </p>
        </div>
        
        <button 
          onClick={() => navigate('/projects')}
          className="rutabaga-button-primary flex items-center gap-2"
        >
          <SafeIcon icon={FiCheck} strokeWidth={3} />
          <span>Done Building</span>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-8">
        {/* Journey Map Section */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-indigo-50 text-indigo-600 rounded-md">
              <SafeIcon icon={FiMap} />
            </div>
            <h2 className="text-lg font-black text-rutabaga-dark uppercase tracking-tight">Journey Map</h2>
          </div>
          
          {hasModule('journey_map') || activeType === 'journey_map' ? (
            <JourneyMapConfig 
              projectId={projectId} 
              existingModule={hasModule('journey_map')} 
              onSave={() => { setActiveType(null); fetchData(); }}
              onCancel={() => setActiveType(null)}
            />
          ) : (
            <button 
              onClick={() => setActiveType('journey_map')}
              className="w-full py-12 rounded-md border-2 border-dashed border-gray-200 text-gray-400 hover:border-rutabaga-pink hover:text-rutabaga-pink transition-all flex flex-col items-center justify-center gap-3 group"
            >
              <SafeIcon icon={FiPlus} className="text-2xl group-hover:scale-110 transition-transform" />
              <span className="text-xs font-black uppercase tracking-widest">Add Journey Map Module</span>
            </button>
          )}
        </section>

        {/* Touchpoint Mapping Section */}
        <section>
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-md">
              <SafeIcon icon={FiGrid} />
            </div>
            <h2 className="text-lg font-black text-rutabaga-dark uppercase tracking-tight">Touchpoint Mapping</h2>
          </div>
          
          {hasModule('xy_plot') || activeType === 'xy_plot' ? (
            <ExperienceMatrixConfig 
              projectId={projectId} 
              existingModule={hasModule('xy_plot')} 
              onSave={() => { setActiveType(null); fetchData(); }}
              onCancel={() => setActiveType(null)}
            />
          ) : (
            <button 
              onClick={() => setActiveType('xy_plot')}
              className="w-full py-12 rounded-md border-2 border-dashed border-gray-200 text-gray-400 hover:border-rutabaga-pink hover:text-rutabaga-pink transition-all flex flex-col items-center justify-center gap-3 group"
            >
              <SafeIcon icon={FiPlus} className="text-2xl group-hover:scale-110 transition-transform" />
              <span className="text-xs font-black uppercase tracking-widest">Add Touchpoint Mapping Module</span>
            </button>
          )}
        </section>
      </div>
    </PageTransition>
  );
};

export default ModuleBuilder;